import fs from 'fs';
import path from 'path';
import { randomUUID } from 'crypto';
import { QueryEngine, ComputedFieldProcessor, TriggerProcessor } from "@dbcube/core";
import { ComputedFieldConfig, DatabaseRecord, DML, MutationInfo, PaginatedResult, RelationOptions, TriggerConfig, WhereCallback, WhereCondition } from "../@types/Database";
import { Trigger } from './Trigger';

/**
 * Main class to handle MySQL database connections and queries.
 * Implements the Singleton pattern to ensure a single instance of the connection pool.
 */
export class Database {
    private name: string;
    private engine: QueryEngine;
    private computedFields: ComputedFieldConfig[];
    private triggers: TriggerConfig[];
    private txId: string | null = null;

    constructor(name: string) {
        this.name = name;
        const engine = new QueryEngine(name);
        this.engine = engine;
        this.computedFields = [];
        this.triggers = [];
    }

    /**
     * Executes raw SQL (MySQL/PostgreSQL/SQLite) or a raw command document (MongoDB)
     * with bound parameters. The escape hatch for anything the builder doesn't cover.
     *
     * @example
     * const rows = await db.raw('SELECT * FROM users WHERE age > ?', [25]);
     * await db.raw('CREATE INDEX idx_users_email ON users(email)');
     */
    async raw<R = DatabaseRecord>(query: string, params: unknown[] = []): Promise<R[]> {
        const response = await this.engine.rawQuery(query, params, this.txId ?? undefined);
        if (response.status != 200) {
            returnFormattedError(response.status, response.message);
            throw new Error(response.message);
        }
        return response.data;
    }

    /**
     * Runs a callback inside a database transaction. Everything executed through
     * the received connection is committed atomically; any thrown error rolls
     * the whole transaction back.
     *
     * Requires daemon mode (enabled by default with an up-to-date query-engine).
     *
     * @example
     * await db.transaction(async (trx) => {
     *     await trx.table('accounts').where('id', '=', 1).update({ balance: 50 });
     *     await trx.table('accounts').where('id', '=', 2).update({ balance: 150 });
     * });
     */
    async transaction<T>(callback: (trx: Database) => Promise<T>): Promise<T> {
        const txId: string = await this.engine.beginTransaction();

        const trx = new Database(this.name);
        trx.engine = this.engine;
        trx.computedFields = this.computedFields;
        trx.triggers = this.triggers;
        trx.txId = txId;

        try {
            const result = await callback(trx);
            await this.engine.commitTransaction(txId);
            return result;
        } catch (error) {
            try {
                await this.engine.rollbackTransaction(txId);
            } catch (rollbackError: any) {
                // El error original manda, pero un rollback fallido no puede
                // pasar en silencio: la transacción puede seguir reteniendo
                // locks hasta que el daemon la expire por inactividad.
                console.error(
                    `[dbcube] Rollback of transaction ${txId} failed: ${rollbackError?.message ?? rollbackError}`
                );
            }
            throw error;
        }
    }

    /**
     * Atomic batch: every write queued in the callback runs inside ONE
     * transaction with a SINGLE engine round-trip (begin/commit included).
     * The callback is synchronous — writes are collected, not awaited.
     * Any failure rolls back everything.
     *
     * This is the fastest way to run a sequence of writes atomically.
     * For transactions that need to READ intermediate results, use
     * `transaction()` (interactive) instead. Triggers/computed fields do
     * not run in batch mode.
     *
     * @example
     * await db.batch(b => {
     *     b.table('accounts').where('id', '=', 1).decrement('balance', 200);
     *     b.table('accounts').where('id', '=', 2).increment('balance', 200);
     * });
     */
    async batch(builder: (b: Database) => void): Promise<MutationInfo[][]> {
        const ops: DML[] = [];
        const collector = new Database(this.name);
        collector.engine = this.engine;
        collector.computedFields = this.computedFields;
        collector.triggers = this.triggers;
        (collector as any)._collectInto = ops;
        builder(collector);
        if (ops.length === 0) return [];

        const response = await this.engine.executeBatch(ops);
        if (response.status !== 200) {
            returnFormattedError(response.status, response.message);
            throw new Error(String(response.message));
        }
        return response.data as MutationInfo[][];
    }

    async useComputes(): Promise<Database> {
        const newDatabase = new Database(this.name); 
        const arrayComputedFields = await ComputedFieldProcessor.getComputedFields(this.name);
        newDatabase.setComputedFields(arrayComputedFields);
        return newDatabase;
    }

    async useTriggers(): Promise<Database> {
        const newDatabase = new Database(this.name); 
        const arrayTriggers = await TriggerProcessor.getTriggers(this.name);
        newDatabase.setTriggers(arrayTriggers);
        return newDatabase;
    }

    async connect(): Promise<void> {
        const response = await this.engine.run('query_engine', [
            '--action', 'connect',
        ]);
        if (response.status != 200) {
            returnFormattedError(response.status, response.message);
        }
    }

    async disconnect(): Promise<void> {
        await this.engine.run('query_engine', [
            '--action', 'disconnect',
        ]);
    }

    /**
     * Creates and returns a new instance of `Table` for the specified table.
     * This method is used to start building queries for a specific table.
     * It provides a fluent interface for common database operations like select, insert, update, and delete.
     *
     * @param {string} tableName - The name of the table to query.
     * @returns {Table} - Returns a new instance of `Table` for the specified table.
     *
     * @example
     * // Select all records from a table
     * const users = await db.table('users').get();
     * 
     * // Select records with conditions
     * const activeUsers = await db.table('users')
     *     .where('status', '=', 'active')
     *     .orderBy('created_at', 'DESC')
     *     .limit(10)
     *     .get();
     * 
     * // Insert records
     * await db.table('users').insert([
     *     { name: 'John', email: 'john@example.com', age: 30 }
     * ]);
     * 
     * // Update records
     * await db.table('users')
     *     .where('id', '=', 1)
     *     .update({ status: 'inactive' });
     * 
     * // Delete records
     * await db.table('users')
     *     .where('status', '=', 'deleted')
     *     .delete();
     * 
     * // Access column management
     * const columns = await db.table('users').columns().get();
     */
    table<T extends DatabaseRecord = DatabaseRecord>(tableName: string): Table<T> {
        const t = new Table<T>(this, this.name, tableName, this.engine, this.computedFields, this.triggers, this.txId);
        const sink = (this as any)._collectInto;
        if (sink) t._batchSink = sink;
        return t;
    }

    private setComputedFields(computedFields: ComputedFieldConfig[]){
        this.computedFields = computedFields;
    }

    private setTriggers(triggers: TriggerConfig[]){
        this.triggers = triggers;
    }
}

/**
 * Class to build and execute SQL queries for a specific table.
 * Supports operations like SELECT, INSERT, UPDATE, DELETE, and more.
 *
 * Generic over the row shape: `db.table<User>('users').get()` resolves to
 * `Promise<User[]>`. Pair it with the interfaces emitted by `dbcube generate`.
 */
export class Table<T extends DatabaseRecord = DatabaseRecord> {
    private engine: QueryEngine;
    private nextType: 'AND' | 'OR' = 'AND';
    private dml: DML;
    private computedFields: ComputedFieldConfig[] = [];
    private trigger: Trigger;
    private triggers: TriggerConfig[];
    private txId: string | null = null;
    private relations: { name: string; options: RelationOptions }[] = [];
    /** Builders de grupo mutan en sitio (ver clone()) */
    private _mutable = false;
    /** En modo batch (db.batch) las escrituras se encolan aquí en vez de
     *  ejecutarse: toda la transacción viaja al engine en UN solo cruce. */
    _batchSink: DML[] | null = null;

    private instance: Database;

    constructor(instance: Database, databaseName: string, tableName: string, engine: QueryEngine, computedFields: ComputedFieldConfig[] = [], triggers: TriggerConfig[] = [], txId: string | null = null) {
        this.engine = engine;
        this.instance = instance;
        this.computedFields = computedFields;
        this.triggers = triggers;
        // Lazy: sin triggers configurados (el caso común) no se construye el
        // runner — db.table() está en el hot path de cada query
        this.trigger = triggers.length > 0 ? new Trigger(instance, databaseName, triggers) : (null as any);
        this.nextType = 'AND';
        this.txId = txId;

        this.dml = {
            type: 'select',
            database: databaseName,
            table: tableName,
            columns: ['*'],
            distinct: false,
            joins: [],
            where: [],
            orderBy: [],
            groupBy: [],
            limit: null,
            offset: null,
            data: null,
            aggregation: null,
            having: []
        };
    }

    /**
     * Specifies the columns to select in a SELECT query.
     *
     * @param {string[]} fields - Array of column names to select. If empty, selects all columns.
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').select(['id', 'name']).get();
     * console.log(users); // [{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }]
     */
    select(fields: string[] = []): Table<T> {
        const clone = this.clone();
        clone.dml.type = 'select';
        clone.dml.columns = fields.length > 0 ? fields : ['*'];
        clone.dml.aggregation = null;
        return clone;
    }

    /**
     * Adds a WHERE condition to the query.
     *
     * @param {string} column - The column to filter by.
     * @param {string} operator - The comparison operator (e.g., '=', '>', '<', 'IS NULL', 'IS NOT NULL').
     * @param {any} value - The value to compare against (optional for IS NULL/IS NOT NULL).
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').where('age', '>', 25).get();
     * const nullUsers = await db.table('users').where('email', 'IS NULL').get();
     * console.log(users); // [{ id: 1, name: 'John', age: 30 }]
     */
    where(column: string, operator: 'IS NULL' | 'IS NOT NULL'): Table<T>;
    where(column: string, operator: '=' | '!=' | '<>' | '>' | '<' | '>=' | '<=' | 'LIKE' | 'NOT LIKE' | 'IN' | 'NOT IN' | 'BETWEEN' | 'NOT BETWEEN', value: any): Table<T>;
    where(column: string, operator: string, value?: any): Table<T> {
        const clone = this.clone();
        clone.dml.where.push({
            column,
            operator,
            value,
            type: clone.nextType,
            isGroup: false
        });
        clone.nextType = 'AND';
        return clone;
    }

    /**
     * Adds an OR WHERE condition to the query.
     *
     * @param {string} column - The column to filter by.
     * @param {string} operator - The comparison operator (e.g., '=', '>', '<', 'IS NULL', 'IS NOT NULL').
     * @param {any} value - The value to compare against (optional for IS NULL/IS NOT NULL).
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').where('age', '>', 25).orWhere('name', '=', 'Jane').get();
     * const nullUsers = await db.table('users').where('active', '=', true).orWhere('email', 'IS NULL').get();
     * console.log(users); // [{ id: 1, name: 'John', age: 30 }, { id: 2, name: 'Jane', age: 25 }]
     */
    orWhere(column: string, operator: 'IS NULL' | 'IS NOT NULL'): Table<T>;
    orWhere(column: string, operator: '=' | '!=' | '<>' | '>' | '<' | '>=' | '<=' | 'LIKE' | 'NOT LIKE' | 'IN' | 'NOT IN' | 'BETWEEN' | 'NOT BETWEEN', value: any): Table<T>;
    orWhere(column: string, operator: string, value?: any): Table<T> {
        const clone = this.clone();
        clone.dml.where.push({
            column,
            operator,
            value,
            type: 'OR',
            isGroup: false
        });
        return clone;
    }

    /**
     * Adds a grouped WHERE condition to the query.
     *
     * @param {WhereCallback} callback - A callback function that receives a new Table instance to build the grouped conditions.
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').whereGroup(query => {
     *     query.where('age', '>', 25).orWhere('name', '=', 'Jane');
     * }).get();
     * console.log(users); // [{ id: 1, name: 'John', age: 30 }, { id: 2, name: 'Jane', age: 25 }]
     */
    whereGroup(callback: WhereCallback): Table<T> {
        const clone = this.clone();
        const groupQuery = new Table(this.instance, clone.dml.database, clone.dml.table, clone.engine);
        // El callback encadena sin usar el valor de retorno; el builder del
        // grupo debe mutar en sitio o las condiciones se perderían en clones
        groupQuery._mutable = true;
        callback(groupQuery);

        clone.dml.where.push({
            type: clone.nextType,
            isGroup: true,
            conditions: groupQuery.dml.where as WhereCondition[]
        });
        clone.nextType = 'AND';
        return clone;
    }

    or(): Table<T> {
        const clone = this.clone();
        clone.nextType = 'OR';
        return clone;
    }

    and(): Table<T> {
        const clone = this.clone();
        clone.nextType = 'AND';
        return clone;
    }

    /**
     * Adds a WHERE BETWEEN condition to the query.
     *
     * @param {string} column - The column to filter by.
     * @param {[any, any]} values - A tuple with two values representing the range.
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').whereBetween('age', [20, 30]).get();
     * console.log(users); // [{ id: 1, name: 'John', age: 30 }, { id: 2, name: 'Jane', age: 25 }]
     */
    whereBetween(column: string, values: [any, any]): Table<T> {
        const clone = this.clone();
        const [value1, value2] = values;
        if (value1 !== undefined && value2 !== undefined) {
            clone.dml.where.push({
                column,
                operator: 'BETWEEN',
                value: [value1, value2],
                type: clone.nextType,
                isGroup: false
            });
            clone.nextType = 'AND';
        }
        return clone;
    }

    /**
     * Adds a WHERE IN condition to the query.
     *
     * @param {string} column - The column to filter by.
     * @param {any[]} values - An array of values to match.
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').whereIn('id', [1, 2]).get();
     * console.log(users); // [{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }]
     */
    whereIn(column: string, values: any[]): Table<T> {
        const clone = this.clone();
        if (Array.isArray(values) && values.length > 0) {
            clone.dml.where.push({
                column,
                operator: 'IN',
                value: values,
                type: clone.nextType,
                isGroup: false
            });
            clone.nextType = 'AND';
        }
        return clone;
    }

    /**
    * Adds a WHERE IS NULL condition to the query.
    *
    * @param {string} column - The column to filter by.
    * @returns {Table} - Returns the current instance of Table for method chaining.
    *
    * @example
    * const users = await db.table('users').whereNull('email').get();
    * console.log(users); // [{ id: 3, name: 'Alice', email: null }]
    */
    whereNull(column: string): Table<T> {
        const clone = this.clone();
        clone.dml.where.push({
            column,
            operator: 'IS NULL',
            type: clone.nextType,
            isGroup: false
        });
        clone.nextType = 'AND';
        return clone;
    }

    /**
     * Adds a WHERE IS NOT NULL condition to the query.
     *
     * @param {string} column - The column to filter by.
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').whereNotNull('email').get();
     * console.log(users); // [{ id: 1, name: 'John', email: 'john@example.com' }]
     */
    whereNotNull(column: string): Table<T> {
        const clone = this.clone();
        clone.dml.where.push({
            column,
            operator: 'IS NOT NULL',
            type: clone.nextType,
            isGroup: false
        });
        clone.nextType = 'AND';
        return clone;
    }

    /**
     * Adds a JOIN clause to the query.
     *
     * @param {string} table - The table to join.
     * @param {string} column1 - The column from the current table.
     * @param {string} operator - The comparison operator (e.g., '=', '>', '<').
     * @param {string} column2 - The column from the joined table.
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').join('orders', 'users.id', '=', 'orders.user_id').get();
     * console.log(users); // [{ id: 1, name: 'John', order_id: 101 }]
     */
    join(table: string, column1: string, operator: string, column2: string): Table<T> {
        const clone = this.clone();
        clone.dml.joins.push({
            type: 'INNER',
            table,
            on: {
                column1,
                operator,
                column2
            }
        });
        return clone;
    }

    /**
     * Adds a LEFT JOIN clause to the query.
     *
     * @param {string} table - The table to join.
     * @param {string} column1 - The column from the current table.
     * @param {string} operator - The comparison operator (e.g., '=', '>', '<').
     * @param {string} column2 - The column from the joined table.
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').leftJoin('orders', 'users.id', '=', 'orders.user_id').get();
     * console.log(users); // [{ id: 1, name: 'John', order_id: 101 }, { id: 2, name: 'Jane', order_id: null }]
     */
    leftJoin(table: string, column1: string, operator: string, column2: string): Table<T> {
        const clone = this.clone();
        clone.dml.joins.push({
            type: 'LEFT',
            table,
            on: {
                column1,
                operator,
                column2
            }
        });
        return clone;
    }

    /**
     * Adds a RIGHT JOIN clause to the query.
     *
     * @param {string} table - The table to join.
     * @param {string} column1 - The column from the current table.
     * @param {string} operator - The comparison operator (e.g., '=', '>', '<').
     * @param {string} column2 - The column from the joined table.
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').rightJoin('orders', 'users.id', '=', 'orders.user_id').get();
     * console.log(users); // [{ id: 1, name: 'John', order_id: 101 }, { id: null, name: null, order_id: 102 }]
     */
    rightJoin(table: string, column1: string, operator: string, column2: string): Table<T> {
        const clone = this.clone();
        clone.dml.joins.push({
            type: 'RIGHT',
            table,
            on: {
                column1,
                operator,
                column2
            }
        });
        return clone;
    }

    /**
     * Adds an ORDER BY clause to the query.
     *
     * @param {string} column - The column to order by.
     * @param {'ASC' | 'DESC'} direction - The sorting direction ('ASC' or 'DESC').
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').orderBy('name', 'ASC').get();
     * console.log(users); // [{ id: 2, name: 'Jane' }, { id: 1, name: 'John' }]
     */
    orderBy(column: string, direction: 'ASC' | 'DESC' = 'ASC'): Table<T> {
        const clone = this.clone();
        const validDirections: ('ASC' | 'DESC')[] = ['ASC', 'DESC'];
        if (validDirections.includes(direction.toUpperCase() as 'ASC' | 'DESC')) {
            clone.dml.orderBy.push({
                column,
                direction: direction.toUpperCase() as 'ASC' | 'DESC'
            });
        } else {
            throw new Error(`Invalid direction: ${direction}. Use 'ASC' or 'DESC'.`);
        }
        return clone;
    }

    /**
     * Adds a GROUP BY clause to the query.
     *
     * @param {string} column - The column to group by.
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').groupBy('age').get();
     * console.log(users); // [{ age: 30, count: 1 }, { age: 25, count: 1 }]
     */
    groupBy(column: string): Table<T> {
        const clone = this.clone();
        clone.dml.groupBy.push(column);
        return clone;
    }

    /**
     * Adds a DISTINCT clause to the query.
     *
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').distinct().select(['name']).get();
     * console.log(users); // [{ name: 'John' }, { name: 'Jane' }]
     */
    distinct(): Table<T> {
        const clone = this.clone();
        clone.dml.distinct = true;
        return clone;
    }

    /**
     * Adds a COUNT clause to the query.
     *
     * @param {string} column - The column to count (default is '*').
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const count = await db.table('users').count().first();
     * console.log(count); // { count: 2 }
     */
    async count(column: string = '*'): Promise<number> {
        const clone = this.clone();
        clone.dml.type = 'select';
        clone.dml.aggregation = {
            type: 'COUNT',
            column,
            alias: 'count'
        }
        
        clone.dml.orderBy = [];  // un agregado escalar no ordena; Postgres rechaza ORDER BY de columnas no agrupadas
clone.dml.columns = [`COUNT(${column}) AS count`];
        clone.dml.data = null;
        clone.dml.limit = null;  // Remover limit para contar todos
        clone.dml.offset = null;  // Remover offset también
        try {
            const result = await clone.getResponse();
            const res = result[0] || null;
            if (res) {
                return res.count;
            }
            return 0;
        }
        catch (error) {
            throw error;
        }
    }

    /**
     * Adds a SUM clause to the query.
     *
     * @param {string} column - The column to sum.
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const totalAge = await db.table('users').sum('age').first();
     * console.log(totalAge); // { sum: 55 }
     */
    async sum(column: string): Promise<number> {
        const clone = this.clone();
        clone.dml.type = 'select';
        clone.dml.aggregation = {
            type: 'SUM',
            column,
            alias: 'sum'
        }
        
        clone.dml.orderBy = [];  // un agregado escalar no ordena; Postgres rechaza ORDER BY de columnas no agrupadas
clone.dml.columns = [`SUM(${column}) AS sum`];
        clone.dml.data = null;
        clone.dml.limit = 1;
        try {
            const result = await clone.getResponse();
            const res = result[0] || null;
            if (res) {
                return res.sum;
            }
            return 0;
        }
        catch (error) {
            throw error;
        }
    }

    /**
     * Adds an AVG clause to the query.
     *
     * @param {string} column - The column to calculate the average.
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const avgAge = await db.table('users').avg('age').first();
     * console.log(avgAge); // { avg: 27.5 }
     */
    async avg(column: string): Promise<number> {
        const clone = this.clone();
        clone.dml.type = 'select';
        clone.dml.aggregation = {
            type: 'AVG',
            column,
            alias: 'avg'
        }
        
        clone.dml.orderBy = [];  // un agregado escalar no ordena; Postgres rechaza ORDER BY de columnas no agrupadas
clone.dml.columns = [`AVG(${column}) AS avg`];
        clone.dml.data = null;
        clone.dml.limit = 1;
        try {
            const result = await clone.getResponse();
            const res = result[0] || null;
            if (res) {
                return res.avg;
            }
            return 0;
        }
        catch (error) {
            throw error;
        }
    }

    /**
     * Adds a MAX clause to the query.
     *
     * @param {string} column - The column to find the maximum value.
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const maxAge = await db.table('users').max('age').first();
     * console.log(maxAge); // { max: 30 }
     */
    async max(column: string): Promise<number> {
        const clone = this.clone();
        clone.dml.type = 'select';
        clone.dml.aggregation = {
            type: 'MAX',
            column,
            alias: 'max'
        }
        
        clone.dml.orderBy = [];  // un agregado escalar no ordena; Postgres rechaza ORDER BY de columnas no agrupadas
clone.dml.columns = [`MAX(${column}) AS max`];
        clone.dml.data = null;
        clone.dml.limit = 1;
        try {
            const result = await clone.getResponse();
            const res = result[0] || null;
            if (res) {
                return res.max;
            }
            return 0;
        }
        catch (error) {
            throw error;
        }
    }

    /**
     * Adds a MIN clause to the query.
     *
     * @param {string} column - The column to find the minimum value.
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const minAge = await db.table('users').min('age').first();
     * console.log(minAge); // { min: 25 }
     */
    async min(column: string): Promise<number> {
        const clone = this.clone();
        clone.dml.type = 'select';
        clone.dml.aggregation = {
            type: 'MIN',
            column,
            alias: 'min'
        }
        
        clone.dml.orderBy = [];  // un agregado escalar no ordena; Postgres rechaza ORDER BY de columnas no agrupadas
clone.dml.columns = [`MIN(${column}) AS min`];
        clone.dml.data = null;
        clone.dml.limit = 1;
        try {
            const result = await clone.getResponse();
            const res = result[0] || null;
            if (res) {
                return res.min;
            }
            return 0;
        }
        catch (error) {
            throw error;
        }
    }

    /**
     * Gets the column names of the table.
     * For SQL databases (MySQL, PostgreSQL, SQLite), returns a simple array of column names.
     * For MongoDB, returns a hierarchical structure with nested documents.
     *
     * @returns {Promise<string[] | any>} - Returns an array of column names for SQL databases,
     *                                      or a hierarchical structure for MongoDB.
     *
     * @example
     * // SQL databases (MySQL, PostgreSQL, SQLite)
     * const columns = await db.table('users').columns();
     * console.log(columns); // ['id', 'name', 'email', 'age', 'created_at']
     *
     * @example
     * // MongoDB
     * const structure = await db.table('users').columns();
     * console.log(structure);
     * // {
     * //   columns: ['id', 'name', 'profile'],
     * //   submenu: {
     * //     profile: {
     * //       columns: ['age', 'city'],
     * //       submenu: {}
     * //     }
     * //   }
     * // }
     */
    async columns(): Promise<string[] | any> {
        const clone = this.clone();
        clone.dml.type = 'columns';
        clone.dml.columns = [];
        clone.dml.where = [];
        clone.dml.orderBy = [];
        clone.dml.groupBy = [];
        clone.dml.limit = null;
        clone.dml.offset = null;
        clone.dml.data = null;
        clone.dml.aggregation = null;

        try {
            const result = await clone.getResponse();

            if (Array.isArray(result) && result.length > 0) {
                // Verificar si es MongoDB (estructura jerárquica)
                if (result[0].columns && result[0].submenu !== undefined) {
                    // MongoDB: devolver la estructura completa
                    return result[0];
                }

                // SQL databases: transformar a array simple de strings
                return result.map((row: any) => row.column_name || row.Field || row.name);
            }

            return [];
        } catch (error) {
            throw error;
        }
    }

    /**
     * Adds a LIMIT clause to the query.
     *
     * @param {number} number - The maximum number of rows to return.
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').limit(1).get();
     * console.log(users); // [{ id: 1, name: 'John', age: 30 }]
     */
    limit(number: number): Table<T> {
        const clone = this.clone();
        clone.dml.limit = Number(number);
        return clone;
    }

    /**
     * Adds pagination to the query using LIMIT and OFFSET.
     *
     * @param {number} number - The page number (starting from 1).
     * @returns {Table} - Returns the current instance of Table for method chaining.
     *
     * @example
     * const users = await db.table('users').limit(1).page(2).get();
     * console.log(users); // [{ id: 2, name: 'Jane', age: 25 }]
     */
    page(number: number): Table<T> {
        const clone = this.clone();
        const pageNum = Number(number);
        if (clone.dml.limit) {
            clone.dml.offset = (pageNum - 1) * clone.dml.limit;
        }
        return clone;
    }

    /**
    * Executes the query and returns all matching rows.
    *
    * @returns {Promise<DatabaseRecord[]>} - Returns an array of rows.
    *
    * @example
    * const users = await db.table('users').get();
    * console.log(users); // [{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }]
    */
    async get(): Promise<T[]> {
        try {
            const clone = this.clone();
            clone.dml.type = 'select';
            clone.dml.data = null;
            clone.dml.aggregation = null;
            const result = await clone.getResponse();
            if (this.relations.length > 0 && Array.isArray(result)) {
                return await this.attachRelations(result) as T[];
            }
            return result as T[];
        }
        catch (error) {
            throw error;
        }
    }

    /**
     * Executes the query and returns the first matching row.
     *
     * @returns {Promise<DatabaseRecord | null>} - Returns the first row or null if no rows match.
     *
     * @example
     * const user = await db.table('users').first();
     * console.log(user); // { id: 1, name: 'John' }
     */
    async first(): Promise<T | null> {
        const clone = this.clone();
        clone.dml.type = 'select';
        clone.dml.data = null;
        clone.dml.aggregation = null;
        clone.dml.limit = 1;
        try {
            const result = await clone.getResponse();
            return result[0] || null;
        }
        catch (error) {
            throw error;
        }
    }

    /**
     * Finds a row by a specific column value.
     *
     * @param {any} value - The value to search for.
     * @param {string} column - The column to search in (default is 'id').
     * @returns {Promise<DatabaseRecord | null>} - Returns the first matching row or null if no rows match.
     *
     * @example
     * const user = await db.table('users').find(1);
     * console.log(user); // { id: 1, name: 'John' }
     */
    async find(value: string | number, column: string = 'id'): Promise<T | null> {
        // Hot path: la query más frecuente de cualquier app. Sin computed
        // fields, triggers ni relaciones pendientes no hay nada que preparar —
        // un DML literal evita la cadena de clones (~0.3-0.5ms por llamada).
        if (this.computedFields.length === 0 && this.relations.length === 0 && !this._mutable) {
            const dml: DML = {
                type: 'select',
                database: this.dml.database,
                table: this.dml.table,
                columns: ['*'],
                distinct: false,
                joins: [],
                where: [{ column, operator: '=', value, type: 'AND', isGroup: false }],
                orderBy: [],
                groupBy: [],
                limit: 1,
                offset: null,
                data: null,
                aggregation: null,
                having: [],
            };
            const response = await this.engine.executeDml(dml, this.txId ?? undefined);
            if (response.status != 200) {
                returnFormattedError(response.status, response.message);
                throw new Error(String(response.message));
            }
            return (response.data?.[0] as T) || null;
        }

        // where() is immutable: reassign the clone or the condition is lost
        const clone = this.clone().where(column, '=', value);
        clone.dml.type = 'select';
        clone.dml.data = null;
        clone.dml.aggregation = null;
        clone.dml.limit = 1;
        try {
            const result = await clone.getResponse();
            return result[0] || null;
        }
        catch (error) {
            throw error;
        }
    }

    /**
     * Inserts one or more rows into the table.
     *
     * @param {DatabaseRecord[]} data - An array of objects representing the rows to insert.
     * @returns {Promise<DatabaseRecord[]>} - Returns an array of the inserted rows.
     *
     * @example
     * const newUsers = await db.table('users').insert([
     *     { name: 'Alice', age: 28 },
     *     { name: 'Bob', age: 32 }
     * ]);
     * console.log(newUsers); // [{ id: 3, name: 'Alice', age: 28 }, { id: 4, name: 'Bob', age: 32 }]
     */
    async insert(data: Partial<T>[]): Promise<T[]> {
        if (!Array.isArray(data)) {
            throw new Error('The insert method requires an array of objects with key-value pairs.');
        }

        if (!data.every(item => typeof item === 'object' && item !== null)) {
            throw new Error('The array must contain only valid objects.');
        }

        // Modo batch: encolar y salir (los triggers/computes no aplican en batch)
        if (this._batchSink) {
            this._batchSink.push({ ...this.dml, type: 'insert', data });
            return undefined as any;
        }

        // Hot path: sin triggers ni computed fields no hay nada que orquestar —
        // ejecutar directo sin la cadena de clones ni getResponse
        if (this.triggers.length === 0 && this.computedFields.length === 0) {
            const dml: DML = { ...this.dml, type: 'insert', data };
            const response = await this.engine.executeDml(dml, this.txId ?? undefined);
            if (response.status != 200) {
                returnFormattedError(response.status, response.message);
                throw new Error(String(response.message));
            }
            return (response.data ?? data) as T[];
        }

        const clone = this.clone();
        clone.dml.type = 'insert';
        clone.dml.data = data;

        const result = await clone.getResponse(clone.dml, 'Add');

        return result ?? data;
    }

    /**
     * Updates rows in the table based on the defined conditions.
     *
     * @param {DatabaseRecord} data - An object with key-value pairs representing the fields to update.
     * @returns {Promise<any>} - Returns the result of the update operation.
     *
     * @example
     * const result = await db.table('users')
     *     .where('id', '=', 1)
     *     .update({ name: 'John Updated', age: 31 });
     * console.log(result); // { affectedRows: 1 }
     */
    async update(data: Partial<T>): Promise<MutationInfo[]> {
        if (typeof data !== 'object' || Array.isArray(data)) {
            throw new Error('The update method requires an object with key-value pairs.');
        }

        if (this.dml.where.length === 0) {
            throw new Error('You must specify at least one WHERE condition to perform an update.');
        }

        if (this._batchSink) {
            this._batchSink.push({ ...this.dml, type: 'update', data });
            return undefined as any;
        }

        if (this.triggers.length === 0 && this.computedFields.length === 0) {
            const dml: DML = { ...this.dml, type: 'update', data };
            const response = await this.engine.executeDml(dml, this.txId ?? undefined);
            if (response.status != 200) {
                returnFormattedError(response.status, response.message);
                throw new Error(String(response.message));
            }
            return response.data as MutationInfo[];
        }

        const clone = this.clone();
        clone.dml.type = 'update';
        clone.dml.data = data;

        return clone.getResponse(clone.dml, 'Update');
    }

    /**
     * Deletes rows from the table based on the defined conditions.
     *
     * @returns {Promise<any>} - Returns the result of the delete operation.
     *
     * @example
     * const result = await db.table('users').where('id', '=', 1).delete();
     * console.log(result); // { affectedRows: 1 }
     */
    async delete(): Promise<MutationInfo[]> {
        if (this.dml.where.length === 0) {
            throw new Error('You must specify at least one WHERE condition to perform a delete.');
        }

        if (this._batchSink) {
            this._batchSink.push({ ...this.dml, type: 'delete', data: null });
            return undefined as any;
        }

        if (this.triggers.length === 0 && this.computedFields.length === 0) {
            const dml: DML = { ...this.dml, type: 'delete', data: null };
            const response = await this.engine.executeDml(dml, this.txId ?? undefined);
            if (response.status != 200) {
                returnFormattedError(response.status, response.message);
                throw new Error(String(response.message));
            }
            return response.data as MutationInfo[];
        }

        const clone = this.clone();
        clone.dml.type = 'delete';

        const deleteData = await clone.getResponse(clone.dml, 'Delete');
        return deleteData;
    }

    /**
     * Adds a WHERE NOT IN condition to the query.
     *
     * @example
     * const users = await db.table('users').whereNotIn('status', ['banned', 'deleted']).get();
     */
    whereNotIn(column: string, values: any[]): Table<T> {
        const clone = this.clone();
        if (Array.isArray(values) && values.length > 0) {
            clone.dml.where.push({
                column,
                operator: 'NOT IN',
                value: values,
                type: clone.nextType,
                isGroup: false
            });
            clone.nextType = 'AND';
        }
        return clone;
    }

    /**
     * Sets an explicit OFFSET for the query (alternative to page()).
     *
     * @example
     * const rows = await db.table('logs').orderBy('id', 'ASC').limit(50).offset(100).get();
     */
    offset(number: number): Table<T> {
        const clone = this.clone();
        clone.dml.offset = Number(number);
        return clone;
    }

    /**
     * Appends raw expressions to the SELECT list (aggregates, functions, aliases).
     * Combine with groupBy() and having() for per-group metrics.
     *
     * @example
     * const stats = await db.table('orders')
     *     .select(['user_id'])
     *     .selectRaw(['COUNT(*) AS order_count', 'SUM(amount) AS total'])
     *     .groupBy('user_id')
     *     .having('order_count', '>', 5)
     *     .get();
     */
    selectRaw(expressions: string[]): Table<T> {
        const clone = this.clone();
        if (clone.dml.columns.length === 1 && clone.dml.columns[0] === '*') {
            clone.dml.columns = [...expressions];
        } else {
            clone.dml.columns = [...clone.dml.columns, ...expressions];
        }
        return clone;
    }

    /**
     * Adds a HAVING condition (filters grouped results).
     *
     * @example
     * .groupBy('user_id').having('COUNT(*)', '>', 5)
     */
    having(column: string, operator: string, value?: any): Table<T> {
        const clone = this.clone();
        clone.dml.having = clone.dml.having ?? [];
        clone.dml.having.push({
            column,
            operator,
            value,
            type: 'AND',
            isGroup: false
        });
        return clone;
    }

    /**
     * Checks if at least one row matches the current conditions.
     * Cheaper than first(): selects a constant with LIMIT 1.
     *
     * @example
     * const taken = await db.table('users').where('email', '=', email).exists();
     */
    async exists(): Promise<boolean> {
        const clone = this.clone();
        clone.dml.type = 'select';
        clone.dml.columns = ['1 AS dbcube_exists'];
        clone.dml.data = null;
        clone.dml.aggregation = null;
        clone.dml.limit = 1;
        clone.dml.offset = null;
        const result = await clone.getResponse();
        return Array.isArray(result) && result.length > 0;
    }

    /**
     * Fetches one page of results plus pagination metadata in a single call.
     * Runs the page query and the total count with the same conditions.
     *
     * @example
     * const { items, total, totalPages, hasNext } = await db.table('products')
     *     .where('published', '=', true)
     *     .orderBy('id', 'ASC')
     *     .paginate(2, 25);
     */
    async paginate(page: number = 1, perPage: number = 20): Promise<PaginatedResult<T>> {
        const pageNum = Math.max(1, Number(page));
        const size = Math.max(1, Number(perPage));

        const itemsQuery = this.limit(size).page(pageNum);
        const [items, total] = await Promise.all([
            itemsQuery.get(),
            this.count()
        ]);

        const totalNum = Number(total);
        const totalPages = Math.ceil(totalNum / size);
        return {
            items,
            page: pageNum,
            perPage: size,
            total: totalNum,
            totalPages,
            hasNext: pageNum < totalPages,
            hasPrev: pageNum > 1,
        };
    }

    /**
     * Processes all matching rows in batches of `size`, keeping memory flat.
     * Return `false` from the callback to stop early.
     *
     * @example
     * await db.table('logs').orderBy('id', 'ASC').chunk(500, async (rows) => {
     *     await processBatch(rows);
     * });
     */
    async chunk(size: number, callback: (rows: T[], page: number) => Promise<void | boolean> | void | boolean): Promise<void> {
        const batchSize = Math.max(1, Number(size));
        let page = 1;
        while (true) {
            const rows = await this.limit(batchSize).page(page).get();
            if (rows.length === 0) break;
            const result = await callback(rows, page);
            if (result === false) break;
            if (rows.length < batchSize) break;
            page++;
        }
    }

    /**
     * Atomically increments a numeric column: `SET col = col + amount`.
     * Requires at least one WHERE condition. Extra fields can be updated in the same statement.
     *
     * @example
     * await db.table('products').where('id', '=', 5).increment('stock', 3);
     * await db.table('posts').where('id', '=', 1).increment('views', 1, { last_viewed_at: new Date().toISOString() });
     */
    async increment(column: string, amount: number = 1, extra: Partial<T> = {}): Promise<MutationInfo[]> {
        if (this.dml.where.length === 0) {
            throw new Error('You must specify at least one WHERE condition to perform an increment.');
        }
        const data = { ...extra, [column]: { $inc: Number(amount) } };

        if (this._batchSink) {
            this._batchSink.push({ ...this.dml, type: 'update', data });
            return undefined as any;
        }

        if (this.triggers.length === 0 && this.computedFields.length === 0) {
            const dml: DML = { ...this.dml, type: 'update', data };
            const response = await this.engine.executeDml(dml, this.txId ?? undefined);
            if (response.status != 200) {
                returnFormattedError(response.status, response.message);
                throw new Error(String(response.message));
            }
            return response.data as MutationInfo[];
        }

        const clone = this.clone();
        clone.dml.type = 'update';
        clone.dml.data = data;
        return clone.getResponse();
    }

    /**
     * Atomically decrements a numeric column: `SET col = col - amount`.
     *
     * @example
     * await db.table('products').where('id', '=', 5).decrement('stock', 1);
     */
    async decrement(column: string, amount: number = 1, extra: Partial<T> = {}): Promise<MutationInfo[]> {
        return this.increment(column, -Math.abs(Number(amount)), extra);
    }

    /**
     * Deletes ALL rows from the table. The only write operation allowed
     * without a WHERE — the destructive intent is explicit in the name.
     *
     * @example
     * await db.table('temp_imports').truncate();
     */
    async truncate(): Promise<MutationInfo[]> {
        const clone = this.clone();
        clone.dml.type = 'delete';
        clone.dml.where = [];
        return clone.getResponse();
    }

    /**
     * Inserts rows, updating them instead when a conflict occurs on the given keys.
     * MySQL → ON DUPLICATE KEY UPDATE · PostgreSQL/SQLite → ON CONFLICT ... DO UPDATE.
     *
     * @param data Rows to insert.
     * @param conflictKeys Column(s) with the UNIQUE/PK constraint that triggers the update.
     * @param updateColumns Columns to overwrite on conflict (defaults to every non-conflict column).
     *
     * @example
     * await db.table('settings').upsert(
     *     [{ key: 'theme', value: 'dark' }],
     *     ['key']
     * );
     */
    async upsert(data: Partial<T>[], conflictKeys: string[], updateColumns?: string[]): Promise<MutationInfo[]> {
        if (!Array.isArray(data) || data.length === 0) {
            throw new Error('The upsert method requires a non-empty array of objects.');
        }
        if (!Array.isArray(conflictKeys) || conflictKeys.length === 0) {
            throw new Error('The upsert method requires at least one conflict key column.');
        }

        const motor: string = this.engine?.getConfig?.()?.type ?? 'mysql';

        const table = this.dml.table;
        const columns = Object.keys(data[0]);
        const targets = updateColumns ?? columns.filter(c => !conflictKeys.includes(c));

        // Único método que arma la operación en JS: validar identificadores
        // porque las claves de los objetos pueden venir de input externo
        assertValidIdentifier(table, 'table');
        for (const c of columns) assertValidIdentifier(c, 'column');
        for (const c of conflictKeys) assertValidIdentifier(c, 'conflict key');
        for (const c of targets) assertValidIdentifier(c, 'update column');

        if (motor === 'mongodb') {
            // Comando `update` nativo con upsert:true por fila — misma semántica
            // que ON CONFLICT: matchea por conflictKeys, actualiza solo targets,
            // y al insertar escribe la fila completa.
            const updates = data.map(row => {
                const q: Record<string, unknown> = {};
                for (const k of conflictKeys) q[k] = row[k];

                const set: Record<string, unknown> = {};
                for (const c of targets) if (c in row) set[c] = row[c];

                const setOnInsert: Record<string, unknown> = {};
                for (const c of columns) {
                    if (!targets.includes(c) && !conflictKeys.includes(c)) setOnInsert[c] = row[c];
                }
                if (!('uuid' in row)) setOnInsert['uuid'] = randomUUID();

                const u: Record<string, unknown> = {};
                if (Object.keys(set).length) u['$set'] = set;
                if (Object.keys(setOnInsert).length) u['$setOnInsert'] = setOnInsert;

                return { q, u, upsert: true, multi: false };
            });

            const command = JSON.stringify({ update: table, updates });
            const response = await this.engine.rawQuery(command, [], this.txId ?? undefined);
            if (response.status != 200) {
                returnFormattedError(response.status, response.message);
                throw new Error(response.message);
            }
            return response.data;
        }

        const quote = (id: string) => motor === 'mysql' ? `\`${id}\`` : `"${id}"`;

        const params: any[] = [];
        const placeholder = () => motor === 'postgres' || motor === 'postgresql'
            ? `$${params.length}`
            : '?';

        const rowsSql = data.map(row => {
            const cells = columns.map(col => {
                params.push(row[col] === undefined ? null : row[col]);
                return placeholder();
            });
            return `(${cells.join(', ')})`;
        }).join(', ');

        const colsSql = columns.map(quote).join(', ');
        let sql: string;

        if (motor === 'mysql') {
            const updates = targets.map(c => `${quote(c)} = VALUES(${quote(c)})`).join(', ');
            sql = `INSERT INTO ${quote(table)} (${colsSql}) VALUES ${rowsSql} ON DUPLICATE KEY UPDATE ${updates}`;
        } else {
            const conflict = conflictKeys.map(quote).join(', ');
            const updates = targets.map(c => `${quote(c)} = excluded.${quote(c)}`).join(', ');
            sql = `INSERT INTO ${quote(table)} (${colsSql}) VALUES ${rowsSql} ON CONFLICT (${conflict}) DO UPDATE SET ${updates}`;
        }

        if (motor === 'sqlite') {
            // SQLite pool schema sync: ensure every pooled connection sees constraints
            // created moments ago (e.g. a UNIQUE index added right before this upsert).
            await this.engine.rawQuery('SELECT name FROM sqlite_master LIMIT 1', [], this.txId ?? undefined);
        }
        const response = await this.engine.rawQuery(sql, params, this.txId ?? undefined);
        if (response.status != 200) {
            returnFormattedError(response.status, response.message);
            throw new Error(response.message);
        }
        return response.data;
    }

    /**
     * Declares a relation to eager-load with the results of get().
     * Relations are resolved from `foreign` definitions in your .cube files,
     * or explicitly via options. Loads each relation with ONE batched query
     * (whereIn) — no N+1.
     *
     * @example
     * // hasMany: orders.user_id → users.id (auto-detected from orders.table.cube)
     * const users = await db.table('users').with('orders').get();
     * // users[0].orders === [{...}, {...}]
     *
     * // belongsTo: posts.author_id → users.id, attached as a single object
     * const posts = await db.table('posts')
     *     .with('author', { table: 'users', foreignKey: 'author_id', type: 'one' })
     *     .get();
     */
    with(relation: string, options: RelationOptions = {}): Table<T> {
        const clone = this.clone();
        clone.relations.push({ name: relation, options });
        return clone;
    }

    private async attachRelations(rows: DatabaseRecord[]): Promise<DatabaseRecord[]> {
        if (rows.length === 0 || this.relations.length === 0) return rows;

        for (const { name, options } of this.relations) {
            const relatedTable = options.table ?? name;
            const cubeMeta = loadCubeRelations();

            // Detect direction + keys from .cube foreign metadata when not explicit
            let type = options.type;
            let foreignKey = options.foreignKey;
            let localKey = options.localKey ?? 'id';

            if (!foreignKey || !type) {
                const childFks = cubeMeta[relatedTable]?.foreigns ?? [];
                const ownFks = cubeMeta[this.dml.table]?.foreigns ?? [];
                const childToParent = childFks.find(fk => fk.table === this.dml.table);
                const parentToChild = ownFks.find(fk => fk.table === relatedTable);

                if (childToParent) {
                    // hasMany: the related table points at us
                    type = type ?? 'many';
                    foreignKey = foreignKey ?? childToParent.column_name;
                    localKey = options.localKey ?? childToParent.column;
                } else if (parentToChild) {
                    // belongsTo: we point at the related table
                    type = type ?? 'one';
                    foreignKey = foreignKey ?? parentToChild.column_name;
                    localKey = options.localKey ?? parentToChild.column;
                } else {
                    // Heuristic fallback: <singular-parent>_id on the child table
                    type = type ?? 'many';
                    foreignKey = foreignKey ?? `${singularize(this.dml.table)}_id`;
                }
            }

            const relatedQuery = new Table(this.instance, this.dml.database, relatedTable, this.engine, this.computedFields, this.triggers, this.txId);

            if (type === 'one') {
                // belongsTo: FK lives on THIS table, match related.localKey
                const fkValues = [...new Set(rows.map(r => r[foreignKey!]).filter(v => v !== null && v !== undefined))];
                const related = fkValues.length > 0
                    ? await relatedQuery.whereIn(localKey, fkValues).get()
                    : [];
                const index = new Map(related.map(r => [r[localKey], r]));
                for (const row of rows) {
                    row[name] = index.get(row[foreignKey!]) ?? null;
                }
            } else {
                // hasMany: FK lives on the RELATED table, match this.localKey
                const ids = [...new Set(rows.map(r => r[localKey]).filter(v => v !== null && v !== undefined))];
                const related = ids.length > 0
                    ? await relatedQuery.whereIn(foreignKey!, ids).get()
                    : [];
                const groups = new Map<any, DatabaseRecord[]>();
                for (const rel of related) {
                    const key = rel[foreignKey!];
                    if (!groups.has(key)) groups.set(key, []);
                    groups.get(key)!.push(rel);
                }
                for (const row of rows) {
                    row[name] = groups.get(row[localKey]) ?? [];
                }
            }
        }
        return rows;
    }

    private async getResponse(dml: any = null, type: any = null): Promise<any> {
        const localDML = dml ? dml : this.dml;
        const computedFieldsNeeded: any[] = [];
        let dependeciesArrray: any[] = [];
        // Columnas pedidas por el usuario ANTES de inyectar dependencias de
        // computed fields: las dependencias solo se eliminan del resultado si
        // el usuario NO las pidió explícitamente
        const requestedColumns = new Set<string>(localDML.columns ?? []);

        if (this.computedFields.length > 0) {
            let columns = localDML.columns;

            for (const field of localDML.columns) {
                const computedField = this.computedFields.find(cf => cf.column === field);

                if (computedField) {
                    computedFieldsNeeded.push(computedField);
                    // Add dependencies to real fields
                    const dependencies = ComputedFieldProcessor.extractDependencies(computedField.instruction);
                    dependeciesArrray = [...dependeciesArrray, ...dependencies];
                    columns = Array.from(new Set([...columns, ...dependencies]));
                    columns = columns.filter((col: string) => col != field);
                }
            }
            localDML.columns = columns;
        }

        let arrayResult = [];
        if (type) {
            const beffore = this.trigger ? this.trigger.get('before' + type) : undefined;
            const after = this.trigger ? this.trigger.get('after' + type) : undefined;
            if (this.triggers.length > 0 && (beffore || after)) {
                const dataset = localDML.data;
                for (let index = 0; index < dataset.length; index++) {
                    const data = dataset[index];
                    const newDML = { ...localDML, data: [data] };
                    if (beffore) {
                        const interceptor = await this.trigger.execute('before' + type, data);
                        const response = await this.engine.executeDml(newDML, this.txId ?? undefined);

                        if (response.status != 200) {
                            interceptor?.discard();
                            returnFormattedError(response.status, response.message);
                            // Un fallo del engine DEBE propagarse: resolver en
                            // silencio haría creer al caller que la escritura
                            // ocurrió (pérdida de datos invisible)
                            throw new Error(String(response.message));
                        }
                        await interceptor?.commit();
                        arrayResult = response.data;
                    }
                    if (after) {

                        const response = await this.engine.executeDml(newDML, this.txId ?? undefined);

                        if (response.status != 200) {
                            returnFormattedError(response.status, response.message);
                            throw new Error(String(response.message));
                        }
                        const interceptor = await this.trigger.execute('after' + type, data);
                        await interceptor?.commit();
                    }
                }
            } else {
                const response = await this.engine.executeDml(localDML, this.txId ?? undefined);

                if (response.status != 200) {
                    returnFormattedError(response.status, response.message);
                    throw new Error(String(response.message));
                }

                arrayResult = response.data;
            }
        } else {
            const response = await this.engine.executeDml(localDML, this.txId ?? undefined);

            if (response.status != 200) {
                returnFormattedError(response.status, response.message);
                throw new Error(String(response.message));
            }

            arrayResult = response.data;
        }

        if (computedFieldsNeeded.length > 0) {
            let newDataset: any = ComputedFieldProcessor.computedFields(arrayResult, computedFieldsNeeded);
            const toRemove = dependeciesArrray.filter(key => !requestedColumns.has(key));
            const result = newDataset.map((obj: any) => {
                const newObj = { ...obj };
                toRemove.forEach(key => delete newObj[key]);
                return newObj;
            });
            return result;
        }
        return arrayResult;
    }

    private clone(): Table<T> {
        // Los builders de grupo (whereGroup) son mutables: el callback encadena
        // sin usar el valor de retorno, así que clonar perdería las condiciones
        if (this._mutable) return this;
        const cloned = Object.create(Object.getPrototypeOf(this));
        cloned.engine = this.engine;
        cloned.instance = this.instance;
        cloned._batchSink = this._batchSink; // el modo batch sobrevive a la cadena de where()
        cloned.nextType = this.nextType;
        cloned.computedFields = this.computedFields;
        cloned.trigger = this.trigger;
        cloned.triggers = this.triggers;
        cloned.txId = this.txId;
        cloned.relations = [...this.relations];

        cloned.dml = {
            ...this.dml,
            columns: [...this.dml.columns],
            joins: [...this.dml.joins],
            where: [...this.dml.where],
            orderBy: [...this.dml.orderBy],
            groupBy: [...this.dml.groupBy],
            having: [...(this.dml.having ?? [])],
            // Clonar propiedades que faltaban para evitar mutación compartida
            data: this.dml.data ? (Array.isArray(this.dml.data) ? [...this.dml.data] : { ...this.dml.data }) : null,
            aggregation: this.dml.aggregation ? { ...this.dml.aggregation } : null
        };
        return cloned;
    }

}

// ---- Cube relation metadata (cached scan of dbcube/*.table.cube) ----

interface CubeForeign {
    column_name: string;   // FK column on the owning table
    table: string;         // referenced table
    column: string;        // referenced column
}

let cubeRelationsCache: Record<string, { foreigns: CubeForeign[] }> | null = null;

function loadCubeRelations(): Record<string, { foreigns: CubeForeign[] }> {
    if (cubeRelationsCache) return cubeRelationsCache;
    const result: Record<string, { foreigns: CubeForeign[] }> = {};

    const scanDir = (dir: string) => {
        let entries: fs.Dirent[];
        try {
            entries = fs.readdirSync(dir, { withFileTypes: true });
        } catch {
            return;
        }
        for (const entry of entries) {
            const full = path.join(dir, entry.name);
            if (entry.isDirectory()) {
                if (entry.name !== 'node_modules' && entry.name !== 'triggers' && entry.name !== 'logs') {
                    scanDir(full);
                }
            } else if (entry.name.endsWith('.table.cube')) {
                try {
                    const content = fs.readFileSync(full, 'utf8');
                    const nameMatch = content.match(/@meta\s*\(\s*\{[\s\S]*?name\s*:\s*"([^"]+)"/);
                    const tableName = nameMatch ? nameMatch[1] : path.basename(entry.name, '.table.cube');

                    const foreigns: CubeForeign[] = [];
                    const colRegex = /(\w+)\s*:\s*\{(?:[^{}]|\{[^{}]*\})*?foreign\s*:\s*\{([^}]*)\}/g;
                    let m: RegExpExecArray | null;
                    while ((m = colRegex.exec(content)) !== null) {
                        const fkTable = m[2].match(/table\s*:\s*"([^"]+)"/);
                        const fkColumn = m[2].match(/column\s*:\s*"([^"]+)"/);
                        if (fkTable) {
                            foreigns.push({
                                column_name: m[1],
                                table: fkTable[1],
                                column: fkColumn ? fkColumn[1] : 'id',
                            });
                        }
                    }
                    result[tableName] = { foreigns };
                } catch { /* unreadable cube — skip */ }
            }
        }
    };

    scanDir(path.join(process.cwd(), 'dbcube'));
    cubeRelationsCache = result;
    return result;
}

function assertValidIdentifier(name: string, kind: string): void {
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) {
        throw new Error(`Invalid ${kind} name: '${name}'. Only letters, numbers and underscore are allowed.`);
    }
}

function singularize(word: string): string {
    if (word.endsWith('ies')) return word.slice(0, -3) + 'y';
    if (word.endsWith('ses')) return word.slice(0, -2);
    if (word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
    return word;
}

function returnFormattedError(status: number, message: string) {
    const RESET = '\x1b[0m';
    const RED = '\x1b[31m';
    const YELLOW = '\x1b[33m';
    const BOLD = '\x1b[1m';
    const CYAN = '\x1b[36m';
    const GRAY = '\x1b[90m';
    const UNDERLINE = '\x1b[4m';
    const MAGENTA = '\x1b[35m';

    let output = '';
    let help = '';
    const color = status === 600 ? YELLOW : RED;


    if (message.includes("[help]")) {
        const parts = message.split("[help]");
        output += `\n${RED}${BOLD}${parts[0]}${RESET}`;
        help += `\n${MAGENTA}${BOLD}[help]${RESET} ${GRAY}${parts[1]}${RESET}\n`;
    } else {
        output += `\n${color}${BOLD}${message}${RESET}\n`;
    }

    const err = new Error();
    const stackLines = err.stack?.split('\n') || [];

    // Buscamos la primera línea del stack fuera de node_modules
    const relevantStackLine = stackLines.find(line =>
        line.includes('.js:') && !line.includes('node_modules')
    );

    if (relevantStackLine) {
        // Los frames pueden venir como `at (path:line:col)` o, en código async,
        // como `at async path:line:col` (sin paréntesis). El `(?:async )?` evita
        // que el "async " quede pegado al path y rompa la lectura del archivo.
        const match = relevantStackLine.match(/\((.*):(\d+):(\d+)\)/) ||
            relevantStackLine.match(/at (?:async )?(.*):(\d+):(\d+)/);

        if (match) {
            const [, filePath, lineStr, columnStr] = match;
            const lineNum = parseInt(lineStr, 10);
            const errorLocation = `${filePath}:${lineStr}:${columnStr}`;

            // Leemos el archivo y sacamos las líneas relevantes
            try {
                const codeLines = fs.readFileSync(filePath, 'utf-8').split('\n');
                const start = Math.max(0, lineNum - 3);
                const end = Math.min(codeLines.length, lineNum + 2);

                output += `\n${CYAN}${BOLD}[code] ${RESET}${YELLOW} ${UNDERLINE}${errorLocation}${RESET}\n`;

                for (let i = start; i < end; i++) {
                    const line = codeLines[i];
                    const lineLabel = `${i + 1}`.padStart(4, ' ');
                    const pointer = i + 1 === lineNum ? `${RED}<-${RESET}` : '  ';
                    output += `${GRAY}${lineLabel}${RESET} ${pointer} ${line}\n`;
                }
            } catch (err) {
                output += `${YELLOW}⚠️ No se pudo leer el archivo de origen: ${filePath}${RESET}\n`;
                output += `\n${CYAN}${BOLD}Stack Trace:${RESET}\n${stackLines.slice(2).join('\n')}\n`;
            }
        }
    }
    output += help;
    console.error(output);
}

export default Database;
